import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { RedirectToLogin } from './LoginModal';

/**
 * Route-level RBAC guard.
 *
 * - Not logged in        → home + open the login modal (no /login page).
 * - Logged in, no perm   → bounce to `/home` (RoleHome). NOT a dead-end 403: RoleHome always resolves to
 *                          a page the user CAN reach (its candidate chain ends at the auth-only /profile,
 *                          and it never routes anyone to a page they'd 403 on), so this can't loop — e.g.
 *                          a FARMER who lands on /warehouse/dashboard is taken to their profile, not stuck.
 * - `anyOf` empty/absent → just requires authentication (self-service pages).
 *
 * SUPER_ADMIN and wildcard permissions always pass (handled in hasPermission).
 */
export function RequirePerm({ anyOf, denyRoles, children }: { anyOf?: string[]; denyRoles?: string[]; children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const hasAny = useAuthStore((s) => s.hasAnyPermission);
  const location = useLocation();

  if (!user) return <RedirectToLogin from={location.pathname + location.search} />;

  // Role denial takes precedence over the permission check: a staff page can be closed to a role even
  // when that role holds a broad/shared permission that also gates it — e.g. a retailer holds STOCK.VIEW
  // (for the catalog's in-stock indicator) but must NOT reach the staff Stock page by URL.
  const roleDenied = !!denyRoles && !!user.role && denyRoles.includes(user.role);
  if (roleDenied || !hasAny(anyOf)) {
    // Already at /home (RoleHome, auth-only) → let it render rather than redirect to itself.
    if (location.pathname === '/home') return <>{children}</>;
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
}
